/**
 * AddForm Utility Functions
 *
 * Pure functions that handle the implementation details.
 * Extracted to keep AddForm.tsx readable and focused on the flow.
 */

import type { BookingCard } from "@/hooks/booking/useBookings";
import type {
  IBooking,
  IPostBooking,
  IServiceCustomer,
  IServiceEmployee,
} from "@/services/types";
import { BookingType } from "@/services/types";
import type { AbsenceAdjustment, AbsenceSummary, AddFormState } from "./types";
import { isPublicHoliday, isWeekendOrHoliday } from "@/utils/date/holidays";

/** Target entity union type */
type TargetEntity =
  | IBooking
  | IServiceCustomer
  | IServiceEmployee
  | null
  | undefined;

/**
 * Context object for buildPostBookings
 * Consolidates related parameters to reduce function complexity
 */
export interface BookingContext {
  targetEntity: TargetEntity;
  isTargetEmployee: boolean;
  isTargetCustomer: boolean;
  isSoftBooking: boolean;
}

// ============================================================
// VALIDATION
// ============================================================

/**
 * Checks if the form has a valid target for submission
 */
export function hasValidTarget(
  state: AddFormState,
  isHardBooking: boolean,
  isSoftBooking: boolean,
  isTargetCustomer: boolean,
): boolean {
  if (state.isAbsence) return true;
  if (isSoftBooking) return isTargetCustomer || state.customer !== null;
  if (isHardBooking) return state.project !== null;
  return false;
}

/**
 * Checks if any day would exceed max hours after adding booking
 */
export function checkOverbookedDays(
  bookingType: string,
  selectedCards: BookingCard[],
  hoursToAdd: number,
  maxHoursPerDay: number,
): boolean {
  if (bookingType !== "hard") return false;

  return selectedCards.some((card) => {
    const currentHours = card.bookings.reduce((sum, b) => sum + b.hours, 0);
    return currentHours + hoursToAdd > maxHoursPerDay;
  });
}

// ============================================================
// ABSENCE DETECTION
// ============================================================

/**
 * Counts weekend days in selection (Saturday and Sunday only)
 */
export function countWeekendDays(selectedCards: BookingCard[]): number {
  return selectedCards.filter((card) => {
    const day = card.date.getDay();
    return day === 0 || day === 6;
  }).length;
}

/**
 * Counts public holidays in selection (excluding regular Saturdays unless they are also holidays)
 */
export function countHolidayDays(selectedCards: BookingCard[]): number {
  return selectedCards.filter((card) => isPublicHoliday(card.date)).length;
}

/**
 * Counts weekend days AND public holidays in selection.
 * Uses isWeekendOrHoliday which includes both weekends and Norwegian public holidays.
 */
export function countWeekendAndHolidayDays(
  selectedCards: BookingCard[],
): number {
  return selectedCards.filter((card) => isWeekendOrHoliday(card.date)).length;
}

/**
 * Gets the list of weekend and holiday cards from selection.
 * Used for filtering out these days if user chooses to skip them.
 */
export function getWeekendAndHolidayCards(
  selectedCards: BookingCard[],
): BookingCard[] {
  return selectedCards.filter((card) => isWeekendOrHoliday(card.date));
}

/**
 * Filters out weekend and holiday days from selection.
 * Used when user chooses to skip booking on weekends/holidays.
 */
export function filterOutWeekendAndHolidays(
  selectedCards: BookingCard[],
): BookingCard[] {
  return selectedCards.filter((card) => !isWeekendOrHoliday(card.date));
}

/**
 * Filters out weekend days and/or public holidays from selection,
 * depending on organization settings / user choice.
 */
export function filterRedDays(
  selectedCards: BookingCard[],
  options: { skipWeekends: boolean; skipHolidays: boolean },
): BookingCard[] {
  const { skipWeekends, skipHolidays } = options;

  return selectedCards.filter((card) => {
    const day = card.date.getDay();
    const isWeekend = day === 0 || day === 6;
    const isHoliday = isPublicHoliday(card.date);

    if (skipWeekends && isWeekend) return false;
    if (skipHolidays && isHoliday) return false;

    return true;
  });
}

/**
 * Counts unique days that should trigger a warning,
 * depending on whether weekends and/or holidays should be skipped.
 *
 * A day that is both a weekend and a holiday is counted only once.
 */
export function countWarningRedDays(
  selectedCards: BookingCard[],
  options: { skipWeekends: boolean; skipHolidays: boolean },
): number {
  const { skipWeekends, skipHolidays } = options;

  return selectedCards.filter((card) => {
    const day = card.date.getDay();
    const isWeekend = day === 0 || day === 6;
    const isHoliday = isPublicHoliday(card.date);

    return (skipWeekends && isWeekend) || (skipHolidays && isHoliday);
  }).length;
}

/**
 * Checks if single day has full absence blocking new bookings
 */
export function hasSingleDayFullAbsence(
  state: AddFormState,
  bookingType: string,
  isTargetEmployee: boolean,
  selectedCards: BookingCard[],
  defaultHours: number,
): boolean {
  if (state.isAbsence || bookingType !== "hard" || !isTargetEmployee)
    return false;
  if (selectedCards.length !== 1) return false;

  const card = selectedCards[0];
  const absenceHours = card.bookings
    .filter((b) => b.b_type === BookingType.ABSENCE)
    .reduce((sum, b) => sum + b.hours, 0);

  return absenceHours >= defaultHours;
}

/**
 * Generates appropriate absence conflict message
 */
export function getAbsenceConflictMessage(
  state: AddFormState,
  bookingType: string,
  isTargetEmployee: boolean,
  selectedCards: BookingCard[],
  defaultHours: number,
): string | null {
  if (state.isAbsence || bookingType !== "hard" || !isTargetEmployee)
    return null;
  if (selectedCards.length === 0) return null;

  const fullAbsenceCount = selectedCards.filter((card) => {
    const absenceHours = card.bookings
      .filter((b) => b.b_type === BookingType.ABSENCE)
      .reduce((sum, b) => sum + b.hours, 0);
    return absenceHours >= defaultHours;
  }).length;

  if (fullAbsenceCount === 0) return null;

  if (selectedCards.length === 1) {
    return "Full absence is already recorded on this day. Remove the absence before adding a booking.";
  }
  if (fullAbsenceCount === selectedCards.length) {
    return "All selected days have full absence. They will be skipped when you continue.";
  }
  if (fullAbsenceCount === 1) {
    return "1 of the selected days has full absence and will be skipped when you continue.";
  }
  return `${fullAbsenceCount} of the selected days have full absence and will be skipped when you continue.`;
}

// ============================================================
// POST BOOKING BUILDER
// ============================================================

/**
 * Calculates absence hours for a booking card.
 * Checks both included and excluded bookings to ensure absence
 * is detected regardless of active project filters.
 */
function getAbsenceHours(card: BookingCard): number {
  const allBookings = [...card.bookings, ...card.excludedBookings];
  return allBookings
    .filter((b) => b.b_type === BookingType.ABSENCE)
    .reduce((sum, b) => sum + b.hours, 0);
}

/**
 * Result from buildPostBookings including absence summary
 */
export interface BuildPostBookingsResult {
  posts: IPostBooking[];
  absenceSummary: AbsenceSummary;
}

/**
 * Builds IPostBooking array for new bookings
 *
 * @param state - Form state with booking details
 * @param selectedCards - Selected booking cards (dates)
 * @param context - Booking context with target entity and type flags
 * @param defaultHours - Default hours per day from org settings
 */
export function buildPostBookings(
  state: AddFormState,
  selectedCards: BookingCard[],
  context: BookingContext,
  defaultHours: number,
): BuildPostBookingsResult {
  const { targetEntity, isTargetEmployee, isTargetCustomer, isSoftBooking } =
    context;

  const skippedDates: Date[] = [];
  const partialAdjustments: AbsenceAdjustment[] = [];

  // Determine employee ID
  const employeeId = isTargetEmployee
    ? (targetEntity as IServiceEmployee).id
    : (selectedCards[0]?.employee?.id ?? null);

  // Soft bookings can proceed without employee
  const allowMissingEmployee =
    isSoftBooking && (isTargetCustomer || state.customer !== null);
  if (!employeeId && !allowMissingEmployee)
    return {
      posts: [],
      absenceSummary: { skippedDates, partialAdjustments },
    };

  const posts: IPostBooking[] = [];

  for (const card of selectedCards) {
    const absenceHours = getAbsenceHours(card);

    // Skip cards with full absence (unless booking absence)
    if (!state.isAbsence && absenceHours >= defaultHours) {
      skippedDates.push(card.date);
      continue;
    }

    // Adjust hours for partial absence
    let hoursToBook = state.hours;
    if (!state.isAbsence && absenceHours > 0 && absenceHours < defaultHours) {
      const remainingHours = defaultHours - absenceHours;
      hoursToBook = Math.min(state.hours, remainingHours);
      partialAdjustments.push({
        date: card.date,
        remainingHours: hoursToBook,
        absenceHours,
        dayHours: defaultHours,
      });
    }

    // Determine booking type and IDs
    let bType: BookingType;
    let projectId: number | null = null;
    let customerId: number | null = null;

    if (state.isAbsence) {
      bType = BookingType.ABSENCE;
      // New absences are created without project association (matching Vue behavior)
      // projectId remains null (initialized above)
    } else if (isSoftBooking) {
      bType = BookingType.SOFT_BOOKING;
      customerId = isTargetCustomer
        ? (targetEntity as IServiceCustomer).id
        : (state.customer?.id ?? null);
    } else {
      bType = BookingType.HARD_BOOKING;
      projectId = state.project?.id ?? null;
    }

    posts.push({
      id: null,
      date: card.date,
      project: projectId,
      employee: employeeId,
      activity: 1,
      b_type: bType,
      hours: hoursToBook,
      customer: customerId,
    });
  }

  return {
    posts,
    absenceSummary: { skippedDates, partialAdjustments },
  };
}

// ============================================================
// HOURS HELPERS
// ============================================================

/**
 * Clamps hours to valid range
 */
export function clampHours(hours: number, maxHours?: number): number {
  const clamped = Math.max(0.5, hours);

  if (maxHours === undefined) {
    return clamped;
  }

  return Math.min(clamped, maxHours);
}
